import {
  organizationProvisioningSource,
  type CodableOrganization,
} from '@src/service/model/OrganizationModel'

export interface OnboardingPolicyInput {
  initialized: boolean
  phoneNumbersLoaded: boolean
  organization: Pick<CodableOrganization, 'provisioningSource'> | null
  isWorkspaceCreator: () => boolean
  phoneNumberCount: number
  invitedWithNumber: boolean
  hasName: boolean
  hasPendingInvites: boolean
  isSubscriptionReviewNull: boolean
  isSubscriptionActive: boolean
  subscriptionNeedsReview: boolean
  hasCompletedActivationFlow: boolean
  firstRunInboxEnabled: boolean
}

export type OnboardingExperience =
  | { status: 'loading' }
  | {
      status: 'ready'
      flow: 'standard'
      needsOnboarding: boolean
      numberStep: 'select-number'
      canRecoverSubscriptionStep: true
      shouldFinishAfterNumberSelection: false
      completionDestination: 'activation' | 'inbox'
    }
  | {
      status: 'ready'
      flow: 'provisioned'
      needsOnboarding: boolean
      step: 'check-subscription' | 'select-number'
      canRecoverSubscriptionStep: false
      shouldFinishAfterNumberSelection: true
      completionDestination: 'inbox'
    }

export function getOnboardingPolicy({
  initialized,
  phoneNumbersLoaded,
  organization,
  isWorkspaceCreator,
  phoneNumberCount,
  invitedWithNumber,
  hasName,
  hasPendingInvites,
  isSubscriptionReviewNull,
  isSubscriptionActive,
  subscriptionNeedsReview,
  hasCompletedActivationFlow,
  firstRunInboxEnabled,
}: OnboardingPolicyInput): OnboardingExperience {
  if (!initialized || !phoneNumbersLoaded) {
    return { status: 'loading' }
  }

  let workspaceCreator: boolean | undefined
  const getIsWorkspaceCreator = () => (workspaceCreator ??= isWorkspaceCreator())
  const usesProvisionedFlow = Boolean(
    organization?.provisioningSource === organizationProvisioningSource.stripeProjects &&
    getIsWorkspaceCreator(),
  )
  const needsNumber = phoneNumberCount === 0 || invitedWithNumber

  if (usesProvisionedFlow) {
    return {
      status: 'ready',
      flow: 'provisioned',
      needsOnboarding: needsNumber,
      step:
        isSubscriptionActive && !subscriptionNeedsReview
          ? 'select-number'
          : 'check-subscription',
      canRecoverSubscriptionStep: false,
      shouldFinishAfterNumberSelection: true,
      completionDestination: 'inbox',
    }
  }

  return {
    status: 'ready',
    flow: 'standard',
    needsOnboarding:
      needsNumber || !hasName || hasPendingInvites || isSubscriptionReviewNull,
    numberStep: 'select-number',
    canRecoverSubscriptionStep: true,
    shouldFinishAfterNumberSelection: false,
    completionDestination:
      firstRunInboxEnabled && !hasCompletedActivationFlow && getIsWorkspaceCreator()
        ? 'activation'
        : 'inbox',
  }
}

export interface WorkspaceToLeaveInput {
  isSubscriptionActive: boolean
  phoneNumberCount: number
  memberCount: number
}

export function hasWorkspaceToLeave({
  isSubscriptionActive,
  phoneNumberCount,
  memberCount,
}: WorkspaceToLeaveInput): boolean {
  return isSubscriptionActive || phoneNumberCount > 0 || memberCount > 1
}
